import jsPDF from 'jspdf';
import '../../../assets/fonts/Inter-pdf/Inter-Bold-normal.js';
import '../../../assets/fonts/Inter-pdf/Inter-Regular-normal.js';
import '../../../assets/fonts/Inter-pdf/Inter-SemiBold-normal.js';

import { PdfInfo, PdfTemplate, PdfLang } from './pdf-types';
import { PALETTES, getSectionTitle } from './pdf-templates';
import { resolveVariables, getMonthName, formatDate } from './pdf-variables';
import {
  buildPaymentText,
  buildLocationText,
  buildCancellationText,
  buildTitleText,
} from './pdf-text-builder';
import { PdfSections } from './pdf-sections';

export type { PdfTemplate, PdfLang };
export type { PdfInfo };

export class Pdf {
  private pdf: jsPDF;
  private y_ref = { y: 10 };
  private sec!: PdfSections;
  private template!: PdfTemplate;
  private lang!: PdfLang;
  private info: any;

  private readonly PAGE_W = 210;
  private readonly M = 20;

  constructor() {
    this.pdf = new jsPDF();
  }

  // ── PRINT PRINCIPALE ──────────────────────────────────────────────────────

  print(
    customer_data: any,
    contract_info: PdfInfo,
    downloadPdf = true,
    openPdf = false,
  ): Blob {

    this.template = contract_info.template ?? 'default';
    this.lang = contract_info.lang ?? ('both' as any);
    const p_palette = PALETTES[this.template];

    this.y_ref = { y: 10 };
    this.sec = new PdfSections(this.pdf, p_palette, this.template, this.y_ref);

    // ── HEADER
    const titleText = buildTitleText(contract_info, this.lang);
    this.sec.drawHeader(this.template, contract_info);
    this.sec.drawFooter(1, contract_info.email);
    this.sec.contractTitle(this.lang);

    // ── BETWEEN
    this.sec.sectionTitle(getSectionTitle('between', this.lang));
    this.pdf.setFont('times', 'bold');
    this.pdf.setFontSize(10);
    this.pdf.setTextColor(...p_palette.dark);
    const titleLines = this.pdf.splitTextToSize(titleText, 170);
    titleLines.forEach((l: string) => {
      this.sec.checkPage();
      this.pdf.text(l, this.M, this.y_ref.y);
      this.sec.nl(5);
    });

    const addrLabel = this.lang === 'it' ? 'Indirizzo' : 'Address';
    this.sec.text(
      `${addrLabel}: ${contract_info.address}`,
      9.5,
      p_palette.mid,
      'times',
      'normal',
    );
    this.sec.text(
      `Tel: ${contract_info.telephone}`,
      9.5,
      p_palette.mid,
      'times',
      'normal',
    );
    this.sec.text(
      `Email: ${contract_info.email}`,
      9.5,
      p_palette.mid,
      'times',
      'normal',
    );
    this.sec.text(
      `P.IVA: ${contract_info.piva}`,
      9.5,
      p_palette.mid,
      'times',
      'normal',
    );
    this.sec.nl(4);

    const andLabel = this.lang === 'it' ? 'E' : 'AND';
    this.pdf.setFont('times', 'bold');
    this.pdf.setFontSize(10);
    this.pdf.setTextColor(...p_palette.dark);
    this.pdf.text(andLabel, this.M, this.y_ref.y);
    this.sec.nl(5);

    const clientLabel =
      this.lang === 'it' ? 'indicata come "Cliente"' : 'referred as "Client"';
    this.sec.text(
      `${customer_data.client.name} ${customer_data.client.lastname} – ${clientLabel}`,
      9.5,
      p_palette.dark,
      'times',
      'normal',
    );

    const dob = customer_data.client.dateOfBirth?.split('-') ?? [];
    const dobLabel = this.lang === 'it' ? 'Data di Nascita' : 'Date of Birth';
    const pobLabel = this.lang === 'it' ? 'Luogo di Nascita' : 'Place of Birth';
    const addrLabelClient = this.lang === 'it' ? 'Indirizzo' : 'Address';

    this.sec.text(
      `${addrLabelClient}: ${customer_data.client.address}`,
      9.5,
      p_palette.mid,
      'times',
      'normal',
    );
    if (dob.length === 3) {
      this.sec.text(
        `${dobLabel}: ${dob[2]}.${dob[1]}.${dob[0]}`,
        9.5,
        p_palette.mid,
        'times',
        'normal',
      );
    }
    this.sec.text(
      `${pobLabel}: ${customer_data.client.placeOfBirth}`,
      9.5,
      p_palette.mid,
      'times',
      'normal',
    );
    this.sec.nl(2);

    // ── DATA EFFICACIA
    const wd = (customer_data.event.date ?? '').split('-');
    this.sec.sectionTitle(getSectionTitle('effective_date', this.lang));
    const effectiveText =
      this.lang === 'it'
        ? `Il presente accordo diventa efficace il ${wd[2]} ${getMonthName(+wd[1], 'it')} ${wd[0]}.`
        : `This agreement shall become effective on ${getMonthName(+wd[1], 'en')} ${wd[2]}, ${wd[0]}.`;
    this.sec.sectionText(effectiveText);

    // ── OGGETTO (testo libero se presente)
    const freeSections = this.resolveSections(contract_info, customer_data);

    if (freeSections['oggetto']) {
      this.sec.sectionText(freeSections['oggetto']);
    }

    // ── SERVIZI
    this.sec.sectionTitle(getSectionTitle('services', this.lang));
    this.renderServices(customer_data, p_palette);

    // ── LOCATION
    this.sec.sectionTitle(getSectionTitle('location', this.lang));
    const locText =
      freeSections['location'] || buildLocationText(contract_info, this.lang);
    this.sec.sectionText(locText);

    // ── PAGAMENTO
    this.sec.sectionTitle(getSectionTitle('pagamento', this.lang));
    const payText =
      freeSections['pagamento'] || buildPaymentText(contract_info, this.lang);
    this.sec.sectionText(payText);

    // ── CANCELLAZIONE
    this.sec.sectionTitle(getSectionTitle('cancellazione', this.lang));
    const canText =
      freeSections['cancellazione'] ||
      buildCancellationText(contract_info, this.lang);
    this.sec.sectionText(canText);

    // ── CLAUSOLE AGGIUNTIVE
    if (freeSections['clausole_aggiuntive']) {
      this.sec.sectionTitle(getSectionTitle('clausole_aggiuntive', this.lang));
      this.sec.sectionText(freeSections['clausole_aggiuntive']);
    }

    // ── CHIUSURA
    if (freeSections['chiusura']) {
      this.sec.sectionTitle(getSectionTitle('chiusura', this.lang));
      this.sec.sectionText(freeSections['chiusura']);
    }

    // ── ALTRO (sezione libera personalizzata dal profilo)
    const altro = contract_info.altro;
    if (altro && (altro.title || altro.text)) {
      this.sec.sectionTitle(altro.title || (this.lang === 'it' ? 'Altro' : 'Other'));
      if (altro.text) this.sec.sectionText(altro.text);
    }

    // ── WEDDING DAY
    this.sec.sectionTitle(getSectionTitle('wedding_day', this.lang));
    const dateLabel = this.lang === 'it' ? 'Data' : 'Date';
    const locationLabel = this.lang === 'it' ? 'Luogo' : 'Location';
    const agreementLabel =
      this.lang === 'it' ? "Data dell'Accordo" : 'Date of Agreement';
    this.sec.text(
      `${dateLabel}: ${wd[2]}.${wd[1]}.${wd[0]}`,
      10,
      p_palette.dark,
      'times',
      'normal',
    );
    this.sec.text(
      `${locationLabel}: ${customer_data.event.location}`,
      10,
      p_palette.dark,
      'times',
      'normal',
    );
    const dd = (customer_data.event.contract?.dateOfContract ?? '').split(
      '-',
    );
    if (dd.length === 3) {
      this.sec.text(
        `${agreementLabel}: ${dd[2]}.${dd[1]}.${dd[0]}`,
        10,
        p_palette.dark,
        'times',
        'normal',
      );
    }
    this.sec.nl(2);

    // ── FIRME
    this.sec.drawSignatures(customer_data, contract_info, this.lang);

    // ── OUTPUT
    const name = `${wd[0]}-${getMonthName(+wd[1], 'en')}-${wd[2]}_${customer_data.client.name}_${customer_data.client.lastname}`;
    if (downloadPdf) this.pdf.save(name);
    this.info = customer_data;
    const blob = this.pdf.output('blob');
    if (openPdf) window.open(URL.createObjectURL(blob), '_blank');
    return blob;
  }

  // ── RISOLVI SEZIONI ───────────────────────────────────────────────────────

  private resolveSections(
    contract_info: PdfInfo,
    customer_data: any,
  ): Record<string, string> {
    const lang = this.lang;
    const resolved: Record<string, string> = {};

    // Sezioni base dal profilo
    const profileSections = contract_info.sections ?? {};
    // Override dal matrimonio (se presenti)
    const brideSections = customer_data.event?.sections ?? {};

    const keys = [
      'oggetto',
      'pagamento',
      'location',
      'cancellazione',
      'clausole_aggiuntive',
      'chiusura',
    ];

    for (const key of keys) {
      // Priorità: override sposa → profilo
      const section = brideSections[key] ?? profileSections[key];
      if (!section) continue;

      // Se è un oggetto bilingue {it, en} prendi la lingua giusta
      if (typeof section === 'object' && (section.it || section.en)) {
        const text =
          lang === 'it'
            ? section.it
            : lang === 'en'
              ? section.en
              : section.it || section.en;
        if (text?.trim()) {
          resolved[key] = resolveVariables(
            text,
            customer_data,
            contract_info,
            lang,
          );
        }
      } else if (typeof section === 'string' && section.trim()) {
        // Legacy: stringa semplice
        resolved[key] = resolveVariables(
          section,
          customer_data,
          contract_info,
          lang,
        );
      }
    }

    return resolved;
  }

  // ── SERVIZI ───────────────────────────────────────────────────────────────

  private renderServices(customer_data: any, palette: any) {
    const p = this.pdf;
    const services: any[] = customer_data.event?.services ?? [];
    const legacyServices: any[] = customer_data.event?.service ?? [];
    const showTotal = !!customer_data.event?.show_total;
    const showPrices = customer_data.event?.show_prices !== false;

    const allItems = services.filter((s: any) => s.selected);

    const testCost = +customer_data.event?.test_cost || 0;
    if (testCost > 0) {
      allItems.push({
        name: this.lang === 'it' ? 'Prova' : 'Trial',
        price: testCost,
        quantity: 1,
      });
    }

    if (allItems.length > 0) {
      allItems.forEach((item: any) => {
        this.sec.checkPage();
        p.setFont('times', 'normal');
        p.setFontSize(10);
        p.setTextColor(palette.dark[0], palette.dark[1], palette.dark[2]);
        // Gestione quantità
        const qty =
          item.quantity && item.quantity > 1 ? ` ×${item.quantity}` : '';
        const qtyN = item.quantity || 1;
        const hasFull =
          showPrices && +item.full_price > +item.price && item.price != null;
        const priceStr =
          showPrices && item.price != null
            ? `  —  €${(+item.price * qtyN).toFixed(2)}`
            : '';
        if (hasFull) {
          const head = `—  ${item.name}${qty}  —  `;
          const full = `€${(+item.full_price * qtyN).toFixed(2)}`;
          const x0 = this.M + 4;
          p.text(head, x0, this.y_ref.y);
          const xFull = x0 + p.getTextWidth(head);
          p.setTextColor(palette.dark[0], palette.dark[1], palette.dark[2]);
          p.text(full, xFull, this.y_ref.y);
          const wFull = p.getTextWidth(full);
          p.setLineWidth(0.25);
          p.setDrawColor(palette.dark[0], palette.dark[1], palette.dark[2]);
          p.line(xFull, this.y_ref.y - 1, xFull + wFull, this.y_ref.y - 1);
          const disc = `  €${(+item.price * qtyN).toFixed(2)}${
            item.discount_name ? `  (${item.discount_name})` : ''
          }`;
          p.text(disc, xFull + wFull, this.y_ref.y);
        } else {
          p.text(`—  ${item.name}${qty}${priceStr}`, this.M + 4, this.y_ref.y);
        }
        this.sec.nl(5);
      });

      if (showTotal) {
        const total = allItems.reduce(
          (s: number, item: any) =>
            s + (+item.price || 0) * (item.quantity || 1),
          0,
        );
        this.sec.nl(2);
        p.setDrawColor(palette.border[0], palette.border[1], palette.border[2]);
        p.setLineWidth(0.3);
        p.line(this.M + 4, this.y_ref.y, this.PAGE_W - this.M, this.y_ref.y);
        this.sec.nl(4);
        p.setFont('times', 'bold');
        p.setFontSize(10);
        p.setTextColor(palette.accent[0], palette.accent[1], palette.accent[2]);
        const totalLabel =
          this.lang === 'it'
            ? `Totale:  €${total.toFixed(2)}`
            : `Total:  €${total.toFixed(2)}`;
        p.text(
          totalLabel,
          this.PAGE_W - this.M - p.getTextWidth(totalLabel),
          this.y_ref.y,
        );
        this.sec.nl(5);
      }
    } else {
      // Legacy
      legacyServices
        .filter((s: any) => s.checked)
        .forEach((s: any) => {
          this.sec.checkPage();
          p.setFont('times', 'normal');
          p.setFontSize(10);
          p.setTextColor(palette.dark[0], palette.dark[1], palette.dark[2]);
          p.text(`—  ${s.service}`, this.M + 4, this.y_ref.y);
          this.sec.nl(5);
        });
    }
    this.sec.nl(2);
  }

  // ── LEGACY ────────────────────────────────────────────────────────────────

  getId(): string {
    return Math.random().toString(36).substring(2, 15);
  }
  getInfo(): any {
    return this.info;
  }
  getPdf(): jsPDF {
    return this.pdf;
  }

  schedule(customer_data: any, image: any, dim: any, save: boolean): any {
    const p = this.pdf;
    p.setFont('Inter-Bold', 'normal');
    p.setFontSize(28);
    this.y_ref.y = 20;
    const tw = p.getTextWidth('NYZED');
    p.text('NYZED', (210 - tw) / 2, this.y_ref.y);
    this.y_ref.y += 10;
    p.setFontSize(20);
    const sw = p.getTextWidth('Wedding organizer');
    p.text('Wedding organizer', (210 - sw) / 2, this.y_ref.y);
    p.setFont('Inter-Regular', 'normal');
    p.setFontSize(15);
    this.y_ref.y += 20;
    p.text('Schedule di: ' + (customer_data.client?.name ?? ''), 20, this.y_ref.y);
    this.y_ref.y += 10;
    const iw = Math.min(190, dim.width);
    const ih = dim.height * (iw / dim.width);
    p.addImage(image, 'JPEG', 10, this.y_ref.y, iw, ih);
    this.y_ref.y += ih + 10;
    if (save) p.save(`Schedule_${customer_data.client?.name ?? 'cliente'}.pdf`);
    return p.output('blob');
  }
}
